// Centralized date helpers. Consolidates parseDate / daysUntil / fmtDate /
// fmtShort / fmtDateTime / isOverdue / overdueDays that previously lived in App.jsx.
// New code should import from here. App.jsx still has the originals during the
// Lane A.2 split; they get removed once routes/components are extracted.

// Real current time. (Was anchored to VAVA_SEED.NOW = 2026-05-11, a demo-era
// constant — which froze every due-date countdown / overdue check at that day,
// so a task due 2026-06-05 showed "还剩 25 天" instead of 1. Regina, 2026-06-04.)
export const NOW = new Date();

export function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  return new Date(s + (s.length === 10 ? 'T00:00:00' : ''));
}

// Whole calendar days from `now` (default: real today) to the due date.
// `now` is injectable so callers/tests can pin it; live code uses real today
// each call so a long-open tab doesn't freeze the countdown.
export function daysUntil(dateStr: string | null | undefined, now: Date = new Date()): number | null {
  if (!dateStr) return null;
  const d = parseDate(dateStr);
  if (!d) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = parseDate(s);
  if (!d) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function fmtShort(s: string | null | undefined): string {
  if (!s) return '';
  const d = parseDate(s);
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}-${dd}`;
}

export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '';
  const d = parseDate(s);
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${mi}`;
}

export interface TaskLike {
  status: string;
  dueDate: string | null;
}

export function isOverdue(task: TaskLike): boolean {
  if (task.status === '已完成' || task.status === '已取消') return false;
  const n = daysUntil(task.dueDate);
  return n !== null && n < 0;
}

export function overdueDays(task: TaskLike): number {
  const n = daysUntil(task.dueDate);
  return n === null ? 0 : Math.abs(n);
}
